import { useLang } from "../../context/LanguageContext";
import { unitImages } from "../../lib/api";
import { useUploadedImages } from "../../hooks/useUploadedImages";
import { Link } from "react-router-dom";
import { Building2 } from "lucide-react";

export default function PropertyLandhaus() {
  const { lang } = useLang();
  const gal = unitImages.landhaus.gallery;
  const { getImages } = useUploadedImages();
  const t = lang === "de" ? DE : EN;

  // Use uploaded exterior images if available, otherwise fallback to Wix
  const uploadedExterior = getImages("landhaus", "exterior");
  const mainImg = uploadedExterior[0] || gal[0];
  const insetImg = uploadedExterior[1] || gal[6] || gal[1];

  return (
    <section id="landhaus" data-testid="landhaus-section" className="relative section-pad bg-[hsl(var(--sage))]">
      {/* Visual separator top */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--foreground))]/15 to-transparent" />

      <div className="container-x">
        {/* Section label */}
        <div className="flex items-center gap-4 mb-14">
          <Building2 className="w-5 h-5 text-[hsl(var(--pine))]" />
          <span className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--pine))]">
            {t.sectionLabel}
          </span>
          <div className="flex-1 h-px bg-[hsl(var(--foreground))]/10" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Left: image + inset */}
          <div className="lg:col-span-6 relative">
            <div className="aspect-[4/5] overflow-hidden" style={{ borderRadius: "4px" }}>
              <img src={mainImg} alt="Landhaus Steiermark" className="w-full h-full object-cover gallery-img" loading="lazy" />
            </div>
            <div className="absolute -bottom-10 -right-6 md:-right-12 w-44 md:w-56 aspect-[3/4] overflow-hidden border-8 border-[hsl(var(--sage))] hidden md:block" style={{ borderRadius: "4px" }}>
              <img src={insetImg} alt="Landhaus interior" className="w-full h-full object-cover gallery-img" loading="lazy" />
            </div>
            {/* Price badge */}
            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-4 py-2.5 shadow-lg" style={{ borderRadius: "4px" }}>
              <div className="text-[10px] tracking-[0.15em] uppercase text-[hsl(var(--foreground))]/60 font-medium">{lang === "de" ? "Ab" : "From"}</div>
              <div className="font-serif text-2xl text-[hsl(var(--pine))]">€350<span className="text-sm font-sans text-[hsl(var(--foreground))]/50">/{lang === "de" ? "Nacht" : "night"}</span></div>
            </div>
          </div>

          {/* Right: content */}
          <div className="lg:col-span-6">
            <div className="overline mb-6 !text-[hsl(var(--pine))]">{t.eyebrow}</div>
            <h2 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight text-[hsl(var(--foreground))] whitespace-pre-line">
              {t.title}
            </h2>
            <p className="mt-8 max-w-lg text-base md:text-lg leading-relaxed text-[hsl(var(--foreground))]/88">
              {t.body}
            </p>

            <ul className="mt-8 space-y-2.5 text-sm text-[hsl(var(--foreground))]/85 max-w-lg">
              {t.features.map((f, i) => (
                <li key={i} data-testid={`landhaus-feature-${i}`} className="flex items-start gap-3">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[hsl(var(--pine))] shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-x-6 md:gap-x-8 gap-y-6 border-t border-[hsl(var(--foreground))]/15 pt-6">
              {t.stats.map((s, i) => (
                <div key={i} data-testid={`landhaus-stat-${i}`}>
                  <div className="font-serif text-3xl md:text-4xl text-[hsl(var(--pine))]">{s.value}</div>
                  <div className="mt-1 text-[11px] tracking-[0.15em] uppercase text-[hsl(var(--foreground))]/88">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/book?unit=landhaus" data-testid="landhaus-cta-book" className="btn-primary !bg-[hsl(var(--pine))] !border-[hsl(var(--pine))] hover:!bg-[hsl(var(--foreground))] hover:!border-[hsl(var(--foreground))]">
                {t.cta} <span aria-hidden>→</span>
              </Link>
              <a
                href="https://wa.me/436641004293?text=Hallo!%20Ich%20interessiere%20mich%20für%20das%20Landhaus%20Steiermark."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="landhaus-cta-whatsapp"
                className="btn-ghost !border-[hsl(var(--foreground))]/40"
              >
                {t.ask}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const DE = {
  sectionLabel: "Unterkunft 2 von 2",
  eyebrow: "Das Landhaus Steiermark",
  title: "Weitläufig,\nfamiliär, alpin.",
  body: "180 m² Wohnfläche mit fünf stilvollen Schlafzimmern, drei modernen Bädern und einem Kaminzimmer mit offenem Feuer. Zwei sonnige Terrassen mit Grillplatz, ein eingezäunter Naturgarten mit Teich und eigenem Hundeauslauf — ideal für Familientreffen, Freundeskreise und Hochzeiten.",
  features: [
    "5 Schlafzimmer mit hochwertigen Massivholzmöbeln",
    "3 moderne Badezimmer mit Dusche",
    "Voll ausgestattete Küche · 5 Sat-Flachbild-TVs · Amazon TV",
    "Kaminzimmer mit offenem Kamin",
    "Eingezäunter Garten mit Teich · Hundeauslauf · Grillplatz",
    "Private Parkplätze · kostenfreies WLAN",
  ],
  stats: [
    { value: "180", label: "m² Wohnfläche" },
    { value: "5",   label: "Schlafzimmer" },
    { value: "12",  label: "Gäste" },
    { value: "3",   label: "Bäder" },
  ],
  cta: "Landhaus buchen",
  ask: "Anfrage per WhatsApp",
};

const EN = {
  sectionLabel: "Property 2 of 2",
  eyebrow: "The Landhaus Steiermark",
  title: "Spacious,\nfamily, alpine.",
  body: "180 m² of living space with five elegant bedrooms, three modern bathrooms and a fireplace lounge. Two sunny terraces with a grill, a fenced nature garden with pond and a dedicated dog run — ideal for family reunions, groups of friends and weddings.",
  features: [
    "5 bedrooms with premium solid-wood furniture",
    "3 modern bathrooms with shower",
    "Fully equipped kitchen · 5 flatscreen Sat-TVs · Amazon TV",
    "Fireplace lounge with open hearth",
    "Fenced garden with pond · dog run · BBQ",
    "Private parking · free Wi-Fi",
  ],
  stats: [
    { value: "180", label: "m² living area" },
    { value: "5",   label: "Bedrooms" },
    { value: "12",  label: "Guests" },
    { value: "3",   label: "Bathrooms" },
  ],
  cta: "Book Landhaus",
  ask: "Ask on WhatsApp",
};
